'use client';

import { useCallback, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useComments } from '@/lib/community/hooks/use-comments';
import { addComment, toggleCommentLike, removeComment } from '@/lib/community/service/comment-service';
import { emitNewReply } from '@/lib/community/reply-event';
import { useAuthContext } from '@/lib/auth-context';
import { useToast } from '@/lib/hooks/use-toast';
import { analytics } from '@/src/utils/analytics';
import { CommentItem } from './comment-item';
import { CommentInput } from './comment-input';

interface CommentsSectionProps {
	postId: string;
	postAuthorId?: string;
	className?: string;
}

export function CommentsSection({ postId, postAuthorId, className = '' }: CommentsSectionProps) {
	const { comments, total, loading } = useComments(postId);
	const { currentUser } = useAuthContext();
	const [submitting, setSubmitting] = useState(false);
	const toast = useToast();
	const likePendingRef = useRef(new Set<string>());

	// ── 全局唯一激活的回复 ID ──
	// null = 无激活, "top" = 顶部输入框激活, 其他 = 某条评论的内联回复
	const [activeReplyId, setActiveReplyId] = useState<string | null>(null);

	// ── 点赞评论（状态锁防重复） ──
	async function handleLikeComment(commentId: string) {
		if (likePendingRef.current.has(commentId)) return;
		likePendingRef.current.add(commentId);
		const comment = comments.find((c) => c.id === commentId) ?? comments.flatMap((c) => c.replies ?? []).find((c) => c.id === commentId);
		const isCurrentlyLiked = comment?.isLiked ?? false;
		try {
			await toggleCommentLike(commentId, isCurrentlyLiked);
			toast.success(isCurrentlyLiked ? '已取消点赞' : '点赞成功');
			analytics.track(isCurrentlyLiked ? 'CommentUnlike' : 'CommentLike', {
				post_id: postId,
				comment_id: commentId,
			});
		} catch {
			toast.error('操作失败，请重试');
		} finally {
			likePendingRef.current.delete(commentId);
		}
	}

	// 顶部输入框获得焦点时激活
	const handleTopInputFocus = useCallback(() => {
		setActiveReplyId('top');
	}, []);

	// ── 提交评论（顶级 + 内联回复通用） ──
	const handleSubmitComment = useCallback(
		async (content: string, parentId?: string) => {
			if (submitting) return;
			setSubmitting(true);
			try {
				const newComment = await addComment(postId, content, parentId);
				// 如果是回复，通知对应的 CommentItem 追加到本地 loadedReplies
				if (parentId) {
					emitNewReply(parentId, newComment);
				}
				toast.success(parentId ? '回复成功' : '评论成功');
				analytics.track(parentId ? 'CommentReply' : 'CommentCreate', {
					post_id: postId,
					...(parentId ? { parent_comment_id: parentId } : {}),
				});
				setActiveReplyId(null);
			} catch {
				toast.error('发布失败，请重试');
			} finally {
				setSubmitting(false);
			}
		},
		[postId, submitting, toast],
	);

	// ── 删除评论（状态锁防重复） ──
	const deletePendingRef = useRef(new Set<string>());
	const handleDeleteComment = useCallback(
		async (commentId: string) => {
			if (deletePendingRef.current.has(commentId)) return;
			deletePendingRef.current.add(commentId);
			try {
				await removeComment(postId, commentId);
				toast.success('已删除评论');
				analytics.track('CommentDelete', {
					post_id: postId,
					comment_id: commentId,
					user_id: currentUser?.userId ?? '',
				});
			} catch {
				toast.error('删除失败，请重试');
			} finally {
				deletePendingRef.current.delete(commentId);
			}
		},
		[postId, currentUser, toast],
	);

	return (
		<div className={`flex flex-col gap-4 ${className}`}>
			{/* Header */}
			<div className="flex items-center gap-2">
				<h3 className="text-xs font-semibold text-foreground">评论 ({loading ? '…' : total})</h3>
				<div className="flex-1 h-px bg-border/20" />
			</div>

			{/* Comment input — 仅用于发布新的顶级评论 */}
			<CommentInput
				inactive={activeReplyId != null && activeReplyId !== 'top'}
				onFocus={handleTopInputFocus}
				onSubmit={handleSubmitComment}
				submitting={submitting}
			/>

			{/* Loading */}
			{loading && (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			)}

			{/* Empty state */}
			{!loading && comments.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">还没有评论，来抢沙发吧 🐾</p>}

			{/* Comment list */}
			{!loading && comments.length > 0 && (
				<div className="flex flex-col gap-4">
					{comments.map((comment) => (
						<CommentItem
							activeReplyId={activeReplyId}
							comment={comment}
							currentUserId={currentUser?.userId}
							key={comment.id}
							onActiveReplyChange={setActiveReplyId}
							onDeleteComment={handleDeleteComment}
							onLikeComment={handleLikeComment}
							onSubmitComment={handleSubmitComment}
							postAuthorId={postAuthorId}
						/>
					))}
				</div>
			)}
		</div>
	);
}
